import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../Protocols/protocolGeneralDetail.scss';
import './marketingSacTranscripts.scss';

const normalizeAnalysisResponse = (payload) => {
  if (!payload) return null;
  if (payload.overallCompliancePercent !== undefined) return payload;
  if (payload.data?.overallCompliancePercent !== undefined) return payload.data;
  if (payload.data?.data?.overallCompliancePercent !== undefined) {
    return payload.data.data;
  }
  return payload;
};

const formatDateTime = (value) => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('es-CL');
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const parsed =
    typeof value === 'string'
      ? Number(value.replace(/[^\d.-]/g, ''))
      : Number(value);
  if (Number.isNaN(parsed)) return `${value}%`;
  return `${Math.floor(parsed)}%`;
};

const MarketingGeneralAnalysisWip = ({ DesignSystemComponents }) => {
  const { Card, Button, Table } = DesignSystemComponents || {};
  const navigate = useNavigate();
  const { state } = useLocation();

  const analysis = normalizeAnalysisResponse(state?.response);

  const scoreText = useMemo(
    () => formatPercent(analysis?.overallCompliancePercent),
    [analysis]
  );

  const scoreBadge = useMemo(() => {
    const score = Number(analysis?.overallCompliancePercent);
    if (Number.isNaN(score)) return null;
    if (score >= 90) return { label: 'Excelente', color: 'success' };
    if (score >= 80) return { label: 'Bueno', color: 'success' };
    if (score >= 70) return { label: 'Aceptable', color: 'warning' };
    return { label: 'Bajo', color: 'danger' };
  }, [analysis]);

  const steps = Array.isArray(analysis?.protocolSteps)
    ? analysis.protocolSteps
    : [];

  const columns = [
    { accessor: 'stepName', label: 'Paso' },
    {
      accessor: 'approvalPercentage',
      label: 'Resultado',
      render: (row) => formatPercent(row.approvalPercentage),
    },
    {
      accessor: 'description',
      label: 'Notas',
      render: (row) => (
        <span className='protocol-step-notes' title={row.description || '-'}>
          {row.description || '-'}
        </span>
      ),
    },
  ];

  if (!Card || !Table) return null;

  return (
    <div className='protocols-page marketing-general-wip-page'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label='Volver'
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button
            type='button'
            className='btn btn-link p-0'
            onClick={() => navigate(-1)}
          >
            Volver
          </button>
        )}
      </div>

      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>An&aacute;lisis General Marketing</h2>
          <h4 className='fw-normal'>
            Resultado del an&aacute;lisis de cumplimiento sobre transcripciones
            seleccionadas.
          </h4>
        </div>
      </div>

      {!analysis && (
        <p className='color-gray-600 mb-4'>
          No hay resultado de an&aacute;lisis para mostrar en esta vista.
        </p>
      )}

      {analysis && (
        <>
          <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
            <div className='protocol-general-summary'>
              <div className='protocol-summary-content'>
                <h3 className='fw-semibold mb-2'>Cumplimiento general</h3>
                <p className='color-gray-600 mb-2'>
                  {analysis.generalSummary ||
                    'No hay resumen disponible para este an&aacute;lisis.'}
                </p>
                <div className='marketing-general-metadata'>
                  <span className='caption'>
                    Fecha solicitud: {formatDateTime(state?.requestedAt)}
                  </span>
                  <span className='caption'>
                    Protocolo: {state?.protocol?.label || '--'}
                  </span>
                  <span className='caption'>
                    Llamadas evaluadas: {analysis.evaluatedCalls ?? '--'}
                  </span>
                </div>
              </div>
              <div className='protocol-summary-score'>
                <div className='protocol-score-circle'>{scoreText}</div>
                {scoreBadge && (
                  <span className={`badge bg-${scoreBadge.color} mt-2`}>
                    {scoreBadge.label}
                  </span>
                )}
              </div>
            </div>
          </Card>

          <Card specialPadding='px-4 pt-4 pb-2'>
            <div className='mb-3'>
              <h3 className='fw-semibold mb-0'>Desglose por Pasos</h3>
            </div>
            <div className='table-container table-general'>
              <Table columns={columns} data={steps} />
            </div>
            {steps.length === 0 && (
              <div className='text-center p-3'>
                <p className='caption font-light mb-0'>
                  No hay pasos evaluados para este an&aacute;lisis.
                </p>
              </div>
            )}
          </Card>

          <Card specialPadding='px-4 pt-4 pb-3' className='mt-4'>
            <div className='mb-2'>
              <h3 className='fw-semibold mb-0'>
                Observaci&oacute;n de Bajo Cumplimiento
              </h3>
            </div>
            <p className='mb-0 color-gray-600'>
              {analysis.lowComplianceNote ||
                'No hay observaciones adicionales para este an&aacute;lisis.'}
            </p>
          </Card>
        </>
      )}
    </div>
  );
};

export default MarketingGeneralAnalysisWip;
